import { textUtil } from '@cmg/common';
import { Box, Tooltip, Typography } from '@cmg/design-system';
import { Link as LinkSharp, LinkOff as LinkOffSharp } from '@mui/icons-material';
import React from 'react';

import { LinkedStatus } from '../../../../../types/domain/entity-matcher/constants';

export type Highlight = 'added' | 'removed' | 'neutral';

export type Props = Readonly<{
  /** A description of the linked status change */
  description?: string;
  /** The name of the firm whose linked status change is originating from */
  from: string;
  /** The name of the firm impacted by the linked status change. */
  to: string | null;
  /** The linked status change value. */
  pendingLinkedStatus: LinkedStatus;
  crmState?: Highlight;
  cmgState?: Highlight;
}>;

const bg = (s: Highlight) => {
  switch (s) {
    case 'added':
      return 'green.100';
    case 'removed':
      return 'red.100';
    case 'neutral':
    default:
      return 'grey.100';
  }
};

const MAX_FIRM_NAME_LENGTH = 18;

export const FirmLinkedStatusChange: React.FC<Props> = ({
  description,
  from,
  to,
  pendingLinkedStatus,
  crmState = 'neutral',
  cmgState = 'neutral',
}) => {
  const fromTruncated = textUtil.truncateAtMaxLength(from ?? '-', {
    maxLength: MAX_FIRM_NAME_LENGTH,
  });
  const toTruncated = textUtil.truncateAtMaxLength(to ?? '-', { maxLength: MAX_FIRM_NAME_LENGTH });

  return (
    <Box>
      {description && <Typography>{description}</Typography>}

      <Box
        alignItems="center"
        columnGap={3}
        gridTemplateColumns="1fr 1fr"
        display="grid"
        position="relative"
      >
        <Tooltip
          title={from && from.length > MAX_FIRM_NAME_LENGTH ? from : ''}
          placement="top"
          variant="info"
        >
          <Typography
            variant="highlight1"
            px={1}
            py={0.5}
            borderRadius={1}
            maxWidth={165}
            minWidth={165}
            sx={{
              bgcolor: bg(crmState),
            }}
            color={theme =>
              crmState === 'removed' ? theme.palette.text.disabled : theme.palette.primary.main
            }
          >
            {fromTruncated}
          </Typography>
        </Tooltip>

        <Tooltip
          title={to && to.length > MAX_FIRM_NAME_LENGTH ? to : ''}
          placement="top"
          variant="info"
        >
          <Typography
            variant="highlight1"
            minWidth={165}
            maxWidth={165}
            borderRadius={1}
            px={1}
            py={0.5}
            sx={{
              bgcolor: bg(cmgState),
            }}
            color={theme =>
              cmgState === 'removed' ? theme.palette.text.disabled : theme.palette.primary.main
            }
          >
            {toTruncated}
          </Typography>
        </Tooltip>

        <Box
          position="absolute"
          left="50%"
          alignItems="center"
          justifyContent="center"
          display="flex"
          sx={{
            transform: 'translateX(-50%)',
          }}
        >
          {pendingLinkedStatus === LinkedStatus.NOT_LINKED ? (
            <LinkOffSharp fontSize="small" sx={{ color: 'text.disabled' }} />
          ) : (
            <LinkSharp fontSize="small" sx={{ color: 'text.disabled' }} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FirmLinkedStatusChange;
